import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, Input, OnInit } from '@angular/core';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { Predecessor, PREDECESSOR_TYPES } from '@tasks/tasks.types';
import { TsDialogComponent } from 'tsui/lib/ts-dialog/ts-dialog.component';
import { TsButtonModule, TsInputComponent } from 'tsui';
import { AsyncPipe, NgIf } from '@angular/common';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { TsSelectModule } from 'tsui/lib/ts-select/ts.select.module';
import { Task } from '@tasks/tasks.types';
import { map, Observable, of } from 'rxjs';
import { TasksService } from '@tasks/services/tasks.service';
import { TaskUtils } from '@tasks/tasks.utils';
import { TsSkeletonComponent } from 'tsui/lib/ts-skeleton/ts-skeleton.component';
import { DurationType } from '@shared/modules/legacy-tasks/tasks.types';
import { TaskStatusService } from '@tasks/services/task-status.service';
import { Store } from '@ngxs/store';
import { UserState } from '@state/user/state';
import { User } from '@core/models/user.types';

@Component({
  selector: 'task-bulk-update',
  standalone: true,
  imports: [
    TsDialogComponent,
    TsButtonModule,
    NgIf,
    FormsModule,
    ReactiveFormsModule,
    TsSelectModule,
    TsInputComponent,
    AsyncPipe,
    TsSkeletonComponent,
  ],
  templateUrl: './task-bulk-update.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TaskBulkUpdateComponent implements OnInit {
  @Input() disabled: boolean;

  protected dialogRef = inject(DialogRef);
  protected dialogData = inject<any>(DIALOG_DATA);
  protected formBuilder = inject(FormBuilder);
  private tasksService = inject(TasksService);
  private cdr = inject(ChangeDetectorRef);
  private taskStatusService = inject(TaskStatusService);
  private store = inject(Store);
  activeUsers$: Observable<User[]> = this.store.select(UserState.getActiveUsers);

  workflowStates;
  users;

  protected bulkTaskForm = this.formBuilder.group({
    workflow_state: [null],
    assigned_user_id: [null],
    following_user_ids: [null],
  });

  ngOnInit(): void {
    this.workflowStates = this.taskStatusService.getAvailableWorkflowStates();
    this.activeUsers$
      .pipe(map((users) => users.map((user) => ({ key: user.id, label: user.name }))))
      .subscribe((users) => {
        this.users = users;
        this.cdr.detectChanges();
      });
  }

  updateTasks(): void {
    this.dialogRef.close(this.getChanges());
  }

  private getChanges(): any {
    const updatedValues: any = {};
    this.getUpdates(this.bulkTaskForm, updatedValues);
    return updatedValues;
  }

  private getUpdates(formItem: FormGroup | FormArray | FormControl, updatedValues: any, name?: string) {
    if (formItem instanceof FormControl) {
      if (name && formItem.dirty) {
        updatedValues[name] = formItem.value;
      }
    } else {
      for (const formControlName in formItem.controls) {
        if (formControlName in formItem.controls) {
          const formControl = formItem.controls[formControlName];

          if (formControl instanceof FormControl) {
            this.getUpdates(formControl, updatedValues, formControlName);
          } else if (formControl instanceof FormArray && formControl.dirty && formControl.controls.length > 0) {
            updatedValues[formControlName] = [];
            this.getUpdates(formControl, updatedValues[formControlName]);
          } else if (formControl instanceof FormGroup && formControl.dirty) {
            updatedValues[formControlName] = {};
            this.getUpdates(formControl, updatedValues[formControlName]);
          }
        }
      }
    }
  }
}
